import classNames from "classnames";
import { useValidation } from "Hooks/useValidation";
import { produce } from "immer";
import { Fragment, useContext, useEffect, useState } from "react";
import HandleEvents from "Util/HandleEvents";
import { isPhone } from "Validators/Functions/Custom";
import { isEmail } from "Validators/Functions/Email";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import { ESignEnvelopeCreateContext, ESignEnvelopeCreateDispatchContext, RecipientDetail } from "../ESignEnvelopeCreate";

export const ESignRecipientStage = () => {
	const { data: { signees } } = useContext(ESignEnvelopeCreateContext);
	const dispatch = useContext(ESignEnvelopeCreateDispatchContext);
	const [isRecipientsStageValid, setIsRecipientsStageValid] = useState<{ [signeeIndex: number]: boolean }>(signees.map(() => false));

	const handleSigneeChange = (index: number, signee: RecipientDetail) => {
		dispatch({
			type: "SetRecipients",
			payload: {
				signees: produce(signees, draft => {
					draft[index] = signee;
				})
			}
		});
	}

	useEffect(() => {
		const allValid = Object.values(isRecipientsStageValid).every(isValid => isValid);
		dispatch({ type: "StageValidation", payload: { stage: "recipients", isValid: allValid } });
	}, [isRecipientsStageValid, dispatch]);

	return (
		<>
			{signees.length === 0 && (<h6 style={{ color: "red" }}>No recipients found as per your selected template. Please update template or use another template.</h6>)}
			{signees.map((signee, index) => (
				<Fragment key={index}>
					<RecipientField
						key={index}
						signee={signee}
						updateSigneeDetail={(value, isValid) => {
							handleSigneeChange(index, value);
							setIsRecipientsStageValid(produce(isRecipientsStageValid, draft => {
								draft[index] = isValid;
							}));
						}}
					/>
				</Fragment>
			))}
		</>
	)
}

export const RecipientField: React.FunctionComponent<{
	signee: RecipientDetail,
	updateSigneeDetail: (value: RecipientDetail, isValid: boolean) => void;
}> = ({ signee, updateSigneeDetail }) => {
	const [showSection, setshowSection] = useState(true);
	const handleChange = (value: string, field: keyof Omit<RecipientDetail, "preFillValues">) => {
		const updatedSignee = produce(signee, draft => {
			draft[field] = value;
	
			// Update prefill fields if they exist
			if (draft.preFillValues) {
				Object.keys(draft.preFillValues).forEach(prefillField => {
					if (prefillField.startsWith(field)) {
						draft.preFillValues[prefillField] = value;
					}
				});
			}
		});
		updateSigneeDetail(updatedSignee, validation.isValid);
	};

	const handlePreFillChange = (value: string, field: string) => {
		// Update all preFillFields that match the base field
		const updatedSignee = produce(signee, draft => {
			if (!draft.preFillValues) {
				draft.preFillValues = {};
			}
			Object.keys(draft.preFillValues).forEach(prefillField => {
				if (prefillField.startsWith(field)) {
					draft.preFillValues[prefillField] = value;
				}
			});
		});
		updateSigneeDetail(updatedSignee, validation.isValid);
	}

	const renderPreFillFields = (preFillFields: { [key: string]: string }) => {
		const preFillFieldsToOmit: string[] = ["name", "email", "phone"];
		const preFillFieldsToRender: { [key: string]: string } = {};

		Object.entries(preFillFields).forEach(([key, value]) => {
			// avoid rendering fields that are already part of signee details
			// and are in the preFillFieldsToOmit array
			// this is to avoid duplication of fields like name, email, phone
			// which are already rendered above
			// it can be name-i, email-i, phone-i
			if (!preFillFieldsToOmit.includes(key && key.split("-")[0])) {
				preFillFieldsToRender[key] = value;
			}
		});

		// also check if any preFillFieldsToRender includes structure like "dl-i", "dl-ii"
		// if so, then we will render those field as one field
		const preFillFieldsGrouped: { [key: string]: string } = {};
		Object.entries(preFillFieldsToRender).forEach(([key, value]) => {
			const baseKey = key.split("-")[0]; // e.g., "dl", "dob"
			if (!preFillFieldsGrouped[baseKey]) {
				preFillFieldsGrouped[baseKey] = value;
			} else {
				preFillFieldsGrouped[baseKey] += `, ${value}`; // concatenate values for the same base key
			}
		});

		function capitalizeFirstLetter(label: string) {
			if (!label) return label;

			// special case for "dl" to "Driver License"
			// for dob to "Date of Birth"
			if (label === "dl" || label.startsWith("dl-")) {
				return "Driver License";
			}
			if (label === "dob" || label.startsWith("dob-")) {
				return "Date of Birth";
			}

			return label.charAt(0).toUpperCase() + label.slice(1);
		}

		if (Object.keys(preFillFieldsGrouped).length === 0) {
			return null; // No pre-fill fields to render
		}

		return (
			<div>
				<h6>Pre-fill Document</h6>
				<span>The document includes fields for the recipient to complete. If you have the details, feel free to pre-fill them in advance.</span>
				{Object.entries(preFillFieldsGrouped).map(([key, value]) => (
					<TextFieldSetter
						key={key}
						label={capitalizeFirstLetter(key)}
						value={value}
						setValue={newValue => handlePreFillChange(newValue, key)}
						placeholder={`Enter ${capitalizeFirstLetter(key)}`}
					/>
				))}
			</div>
		);
	}

	const validation = useValidation({
		"name": {
			value: signee.name,
			isRequired: true,
			requiredText: "Please enter a full name.",
		},
		"email": {
			value: signee.email,
			requiredText: "Please enter an email address.",
			validate: v => v.assertTruthy("Please enter an email address.") && v.assertRule(r => !r || isEmail(r), "Please enter a valid email address."),
		},
		"phone": {
			value: signee.phone,
			isRequired: false,
			validate: v => v.assertRule(r => !r ||  isPhone(r), "Please enter a valid phone number."),
		}
	});

	return (
		<div className="business-information">
			<div className="section-title-container">
				<h6 className="section-title">Recipient Details</h6>
				<span
					className={classNames('accordion-icon icon-only icon-chevron-down', {
						open: showSection,
					})}
					{...HandleEvents(() => setshowSection(prev => !prev))}
				/>
			</div>
			{showSection && (
				<>
					<h6>Contact Detail</h6>
					<TextFieldSetter
						label="Full name"
						value={signee.name}
						setValue={value => handleChange(value, "name")}
						isRequired
						placeholder="Jane Smith"
						errors={validation.get("name")[0]}
					/>
					<TextFieldSetter
						label="Email address"
						value={signee.email}
						setValue={value => handleChange(value, "email")}
						isRequired
						placeholder="Email Address"
						errors={validation.get("email")[0]}
					/>
					<TextFieldSetter
						label="Phone number"
						value={signee.phone}
						setValue={value => handleChange(value, "phone")}
						placeholder="Phone Number"
						errors={validation.get("phone")[0]}
					/>
					{renderPreFillFields(signee.preFillValues)}
				</>
			)}
		</div>
	)
}